import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../AuthContext.jsx';
import { api } from '../api.js';
import { useTimer } from '../lib/useTimer.js';
import ScrambleBox from '../components/ScrambleBox.jsx';
import SolveList from '../components/SolveList.jsx';
import { nextScramble } from '@shared/scramble.js';
import { EVENTS, eventByKey } from '@shared/events.js';
import { formatTime } from '@shared/format.js';
import { effectiveTime, trimmedAverage2, bestSingle } from '@shared/stats.js';

const SESSION_KEY = 'cube_active_session';

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function readStoredSession() {
  try {
    return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
  } catch {
    return null;
  }
}

export default function TimerPage() {
  const { user } = useAuth();
  const [event, setEvent] = useState(user?.main_event || '3x3');
  const [scramble, setScramble] = useState(() => nextScramble(user?.main_event || '3x3'));
  const [session, setSession] = useState(null); // {id, event_type, solves:[...], ...}
  const [overview, setOverview] = useState(null);
  const [goal, setGoal] = useState(eventByKey(event).defaultSolves);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [msg, setMsg] = useState('');
  const busyRef = useRef(false);
  const eventRef = useRef(event);
  eventRef.current = event;

  const timer = useTimer();

  const loadOverview = useCallback(async (ev) => {
    try {
      const data = await api(`/stats/overview?event=${ev}`);
      setOverview(data.overview);
    } catch {
      /* ignore */
    }
  }, []);

  // 切换项目：换打乱、换目标、重置计时器
  const switchEvent = (ev) => {
    if (ev === eventRef.current) return;
    setEvent(ev);
    setScramble(nextScramble(ev));
    setGoal(eventByKey(ev).defaultSolves);
    setSession(null);
    setSaved(null);
    timer.reset();
  };

  useEffect(() => {
    loadOverview(event);
    // 尝试恢复今天同项目的打卡
    const stored = readStoredSession();
    if (stored && stored.event === event && stored.day === todayKey()) {
      api(`/sessions/${stored.sessionId}`)
        .then((d) => {
          if (d.session && d.session.event_type === event) setSession(d.session);
        })
        .catch(() => {});
    }
  }, [event, loadOverview]);

  const flash = (text) => {
    setMsg(text);
    setTimeout(() => setMsg(''), 2200);
  };

  // 保存成绩：先确保有打卡，再追加
  const saveResult = useCallback(
    async (penalty) => {
      if (busyRef.current) return;
      busyRef.current = true;
      setBusy(true);
      try {
        let current = session;
        if (!current || current.event_type !== eventRef.current) {
          const created = await api('/sessions', { method: 'POST', body: { event_type: eventRef.current } });
          current = created.session;
          localStorage.setItem(
            SESSION_KEY,
            JSON.stringify({ sessionId: current.id, event: eventRef.current, day: todayKey() })
          );
          setSession(current);
        }
        const data = await api(`/sessions/${current.id}/solves`, {
          method: 'POST',
          body: { scramble, time_ms: Math.round(timer.resultMs || 0), penalty },
        });
        setSession(data.session);
        setSaved({ time: timer.resultMs, penalty });
        setScramble(nextScramble(eventRef.current));
        timer.reset();
        loadOverview(eventRef.current);
        flash(penalty === 'dnf' ? '已记录 DNF，再来一把' : '成绩已保存 ✓');
      } catch (err) {
        flash(err.message || '保存失败');
      } finally {
        busyRef.current = false;
        setBusy(false);
      }
    },
    [session, scramble, timer, loadOverview]
  );

  const deleteSolve = async (solve) => {
    if (!session) return;
    try {
      const data = await api(`/solves/${solve.id}`, { method: 'DELETE' });
      setSession(data.session);
      loadOverview(event);
    } catch (err) {
      flash(err.message);
    }
  };

  const endCheckin = () => {
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
    setSaved(null);
    flash('本次打卡已结束，下次开始自动开启新打卡');
  };

  const ev = eventByKey(event);
  const sessionSolves = session?.solves || [];
  const sessionAo5 = sessionSolves.length >= 5 ? trimmedAverage2(sessionSolves.slice(-5)) : null;
  const sessionAvg = (() => {
    const v = sessionSolves.map(effectiveTime).filter((t) => t !== null);
    return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
  })();
  const doneCount = sessionSolves.length;

  const timerColor =
    timer.state === 'ready' ? 'text-danger'
    : timer.state === 'green' ? 'text-good'
    : timer.state === 'running' ? 'text-accent'
    : timer.state === 'stopped' ? 'text-slate-50'
    : 'text-slate-600';

  const statusText =
    timer.state === 'idle' ? '按住空格或屏幕准备'
    : timer.state === 'ready' ? '保持按住…'
    : timer.state === 'green' ? '松开后按空格 / 点击开始'
    : timer.state === 'running' ? '再次按下停止'
    : '选择结果';

  const showTime = timer.state === 'stopped' ? timer.resultMs : timer.displayMs;

  return (
    <div className="space-y-5">
      {/* 项目切换 */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-xl font-bold text-slate-100">速拧计时</h1>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
          {EVENTS.map((e) => (
            <button
              key={e.key}
              onClick={() => switchEvent(e.key)}
              className={`tab ${event === e.key ? 'tab-active' : 'tab-idle'}`}
            >
              {e.name}
            </button>
          ))}
        </div>
      </div>

      {msg && (
        <div className="text-sm bg-good/10 text-good border border-good/20 rounded-xl px-4 py-2.5 animate-pulse">
          {msg}
        </div>
      )}

      {/* 顶部统计 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatMini label="单次 PB" value={overview?.pb_single != null ? formatTime(overview.pb_single) : '—'} accent />
        <StatMini label="当前 Ao5" value={overview?.current_ao5 != null ? formatTime(overview.current_ao5) : '—'} />
        <StatMini label="当前 Ao12" value={overview?.current_ao12 != null ? formatTime(overview.current_ao12) : '—'} />
        <StatMini label="今日打卡" value={`${overview?.today_solves ?? 0} 把`} />
      </div>

      <ScrambleBox scramble={scramble} onNew={() => setScramble(nextScramble(event))} eventName={ev.name} />

      {/* 计时器 */}
      <div
        className="timer-touch card relative flex flex-col items-center justify-center py-14 sm:py-20 cursor-pointer select-none overflow-hidden"
        onMouseDown={timer.press}
        onMouseUp={timer.release}
        onTouchStart={(e) => {
          e.preventDefault();
          timer.press();
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          timer.release();
        }}
        style={{ minHeight: 280 }}
      >
        <div
          className={`absolute inset-x-0 top-0 h-1 transition-colors duration-200 ${
            timer.state === 'ready' ? 'bg-danger'
            : timer.state === 'green' ? 'bg-good'
            : timer.state === 'running' ? 'bg-accent'
            : 'bg-transparent'
          }`}
        />
        <div className={`font-mono tabular-nums font-bold transition-colors duration-150 ${timerColor}`}
          style={{ fontSize: 'clamp(64px, 16vw, 130px)', lineHeight: 1.1 }}>
          {formatTime(showTime)}
        </div>
        <div className="mt-4 text-sm text-slate-500 h-5">{statusText}</div>

        {timer.state === 'stopped' && (
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 animate-[fadeIn_.2s_ease]">
            <button className="btn-primary" disabled={busy} onClick={() => saveResult('none')}>
              ✓ 正常
            </button>
            <button className="btn-ghost" disabled={busy} onClick={() => saveResult('+2')}>
              +2 罚时
            </button>
            <button className="btn-ghost" disabled={busy} onClick={() => saveResult('dnf')}>
              DNF
            </button>
            <button className="btn-ghost text-slate-400" onClick={timer.reset}>
              重试
            </button>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* 本次打卡 */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-slate-100">本次打卡</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {session ? `开始于 ${new Date(session.created_at).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })}` : '尚未开始'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                className="input !w-24 !py-1.5 !text-xs"
                value={goal}
                onChange={(e) => setGoal(Number(e.target.value))}
                title="本次打卡目标把数"
              >
                {[1, 3, 5, 12].map((n) => (
                  <option key={n} value={n}>{n} 把/组</option>
                ))}
              </select>
              {session && (
                <button className="btn-ghost !py-1.5 !text-xs" onClick={endCheckin}>
                  结束打卡
                </button>
              )}
            </div>
          </div>

          <div className="mb-4">
            <div className="flex justify-between text-xs text-slate-400 mb-1.5">
              <span>目标进度</span>
              <span className="font-mono">{doneCount} / {goal}</span>
            </div>
            <div className="h-2 rounded-full bg-slate-700/50 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${doneCount >= goal ? 'bg-good' : 'bg-accent'}`}
                style={{ width: `${Math.min(100, (doneCount / goal) * 100)}%` }}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4 text-center">
            <MiniStat label="本次平均" value={sessionAvg != null ? formatTime(sessionAvg) : '—'} />
            <MiniStat label="本次 Ao5" value={sessionAo5 != null ? formatTime(sessionAo5) : '—'} />
            <MiniStat label="本次最佳" value={sessionSolves.length ? formatTime(bestSingle(sessionSolves)) : '—'} />
          </div>

          <div className="max-h-64 overflow-y-auto">
            <SolveList solves={sessionSolves} onDelete={deleteSolve} dense />
          </div>
        </div>

        {/* 使用说明 */}
        <div className="card p-5 text-sm text-slate-400 space-y-3">
          <h2 className="font-semibold text-slate-100">操作说明（csTimer 风格）</h2>
          <ol className="space-y-2 list-decimal list-inside">
            <li>按住 <kbd className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-200 text-xs">空格</kbd> 或长按屏幕，计时器变 <span className="text-danger">红</span>。</li>
            <li>保持按住 0.5 秒后松开，计时器变 <span className="text-good">绿</span>，表示可以开始。</li>
            <li>按下 <kbd className="px-1.5 py-0.5 rounded bg-slate-700 text-slate-200 text-xs">空格</kbd> 或点击，开始计时。</li>
            <li>再次按下停止，选择 <span className="text-slate-200">正常 / +2 / DNF</span> 保存成绩。</li>
            <li>每把结束后自动生成下一个打乱公式，连续完成一组即可完成打卡。</li>
          </ol>
          <div className="pt-2 border-t border-slate-700/50">
            <p className="text-xs text-slate-500">
              小贴士：计时采用 <code className="text-accent">performance.now()</code> 高精度时间；换项目、换打乱不影响已保存成绩。
            </p>
          </div>
          {saved && (
            <div className="bg-slate-700/30 rounded-xl p-3 text-xs text-slate-400">
              上一把：<span className="font-mono text-slate-200">{saved.penalty === 'dnf' ? 'DNF' : formatTime(saved.time + (saved.penalty === '+2' ? 2000 : 0))}</span>
              {saved.penalty === '+2' && <span className="text-warn ml-1">(+2)</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatMini({ label, value, accent = false }) {
  return (
    <div className="card px-4 py-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-lg font-semibold ${accent ? 'text-accent' : 'text-slate-100'}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-0.5 font-mono text-base text-slate-100">{value}</div>
    </div>
  );
}
